// @ts-nocheck

import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoleAssignments } from "@/hooks/useUserRoleAssignments";
import { useParentRelationships } from "@/hooks/useParentRelationships";
import { usePressMemberships } from "@/hooks/usePressMemberships";
import { useRole } from "@/contexts/RoleContext";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  HelpCircle, 
  Settings, 
  LayoutDashboard, 
  UserCog, 
  Users, 
  CreditCard, 
  LogOut,
  ChevronRight,
  Bell,
  Key,
  Shield,
  Building2,
  Calendar,
  MailPlus
} from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import AreaUserMenu from '@/components/common/AreaUserMenu';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface NavbarProps {
  toggleSidebar: () => void;
  isAdmin?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ toggleSidebar, isAdmin = false }) => {
  const { profile, signOut, user } = useAuth();
  const { allowedDashboardPaths, isLoading } = useUserRoleAssignments();
  const { relationships } = useParentRelationships();
  const { hasMembership, parentMemberships } = usePressMemberships();
  const { isSuperAdmin, isTenantAdmin, isTenantStaff } = useRole();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user is currently in club coach dashboard context
  const isClubCoachContext = location.pathname.startsWith('/club-coach-dashboard');

  // If no user is authenticated, show a simple public navbar
  if (!user) {
    return (
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <h1 className="text-xl font-bold text-primary cursor-pointer" onClick={() => navigate('/')}>
              Recruit<span className="text-secondary">Look</span>
            </h1>
            <nav className="hidden md:flex items-center gap-4">
              <Button variant="ghost" size="sm" asChild>
                <a href="https://recruitlook.com/athletes" target="_blank" rel="noopener noreferrer">
                  Athletes
                </a>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/events')}>
                Events
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/tournaments')}>
                Tournaments
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/press')}>
                Press
              </Button>
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/login')}>
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleAccountClick = () => {
    navigate("/account");
  };

  // Check if user has parent role and active athlete relationships
  const hasParentRole = profile?.role === 'parent';
  const activeAthleteRelationships = relationships?.filter(r => 
    r.status === 'approved' && r.access_level === 'full'
  ) || [];

  // Check if user has an active press membership (any press membership)
  const hasActivePressMembership = parentMemberships.some(membership => 
    membership.is_active && membership.membership_slug.includes('press')
  );

  // Create dashboard menu items based on allowed paths
  const getDashboardMenuItems = () => {
    const dashboardItems = [];
    
    // Always show Athlete Dashboard for athletes or if it's in allowed paths
    if (profile?.role === 'athlete' || allowedDashboardPaths.includes('/athlete-dashboard')) {
      dashboardItems.push({
        label: 'Athlete Dashboard',
        path: '/athlete-dashboard'
      });
    }
    
    // Show Press Dashboard if user has active press membership (after athlete dashboard)
    if (hasActivePressMembership) {
      dashboardItems.push({
        label: 'Press Dashboard',
        path: '/press-dashboard'
      });
    }
    
    if (allowedDashboardPaths.includes('/parent-dashboard')) {
      dashboardItems.push({
        label: 'Parent Dashboard',
        path: '/parent-dashboard'
      });
    }
    
    // Add athlete dashboard access for parents with full access relationships
    if (hasParentRole && activeAthleteRelationships.length > 0) {
      console.log("🔄 Parent dashboard switch: Adding athlete dashboard options for parent", {
        parentProfileId: profile?.id,
        approvedRelationships: activeAthleteRelationships.length
      });
      
      // Add each athlete relationship as a separate menu item
      activeAthleteRelationships.forEach((relationship) => {
        dashboardItems.push({
          label: `Athlete Dashboard (${relationship.athlete?.name || 'Athlete'})`,
          path: `/athlete-dashboard?profile_id=${relationship.athlete_profile_id}&asParent=1`
        });
      });
    }
    
    if (allowedDashboardPaths.includes('/admin-dashboard')) {
      dashboardItems.push({
        label: 'Admin Dashboard',
        path: '/admin-dashboard'
      });
    }
    
    if (allowedDashboardPaths.includes('/college-coach-dashboard')) {
      dashboardItems.push({
        label: 'College Coach Dashboard',
        path: '/college-coach-dashboard'
      });
    }
    
    if (allowedDashboardPaths.includes('/club-coach-dashboard')) {
      dashboardItems.push({
        label: 'Club Coach Dashboard',
        path: '/club-coach-dashboard'
      });
    }
    
    if (allowedDashboardPaths.includes('/vendor-press-dashboard')) {
      dashboardItems.push({
        label: 'Vendor Dashboard',
        path: '/vendor-press-dashboard'
      });
    }
    
    return dashboardItems;
  };

  const dashboardItems = getDashboardMenuItems();
  const showDashboardSection = !isLoading && dashboardItems.length > 0;

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  const handleResetPassword = async () => {
    if (!profile?.email) {
      toast.error('Email not found');
      return;
    }
    
    try {
      const { error } = await invokeEdgeFunction('send-password-reset', {
        body: { email: profile.email }
      });
      
      if (error) throw error;
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Error sending reset email:', error);
      toast.error('Failed to send password reset email');
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="md:hidden">
          <Button variant="ghost" size="sm" onClick={toggleSidebar}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-6 h-6"
            >
              <path
                fillRule="evenodd"
                d="M3 6.75A.75.75 0 013.75 6h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 6.75zM3 12A.75.75 0 013.75 11.25h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 12zm-1.5 5.25a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H2.25a.75.75 0 01-.75-.75z"
                clipRule="evenodd"
              />
            </svg>
            <span className="sr-only">Open sidebar</span>
          </Button>
        </div>
        <div></div>
        <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <Bell className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{profile?.name}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {showDashboardSection && (
              <>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    My Dashboards
                    <ChevronRight className="ml-auto h-4 w-4" />
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {dashboardItems.map((item, index) => (
                      <DropdownMenuItem 
                        key={`${item.path}-${index}`}
                        onClick={() => navigate(item.path)}
                        className="cursor-pointer"
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
              </>
            )}

            {/* RLevents Super Admin menu */}
            {isSuperAdmin && (
              <>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Shield className="mr-2 h-4 w-4" />
                    Admin
                    <ChevronRight className="ml-auto h-4 w-4" />
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onClick={() => navigate('/admin/events')} className="cursor-pointer">
                      <Calendar className="mr-2 h-4 w-4" />
                      Events
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/admin/invitations')} className="cursor-pointer">
                      <MailPlus className="mr-2 h-4 w-4" />
                      Invitations
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/admin/teams/merge')} className="cursor-pointer">
                      <Users className="mr-2 h-4 w-4" />
                      Merge teams
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
              </>
            )}

            {/* RLevents Tenant Admin / Staff menu (also shown alongside Admin if user has both) */}
            {(isTenantAdmin || isTenantStaff) && (
              <>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Building2 className="mr-2 h-4 w-4" />
                    Manage
                    <ChevronRight className="ml-auto h-4 w-4" />
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onClick={() => navigate('/manage/events')} className="cursor-pointer">
                      <Calendar className="mr-2 h-4 w-4" />
                      Events
                    </DropdownMenuItem>
                    {isTenantAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/manage/invitations')} className="cursor-pointer">
                        <MailPlus className="mr-2 h-4 w-4" />
                        Invitations
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
              </>
            )}
            
            {profile?.role === 'athlete' && (
              <DropdownMenuItem onClick={() => navigate('/athlete-dashboard')}>
                <LayoutDashboard className="mr-2 h-4 w-4" />
                Athlete Dashboard
              </DropdownMenuItem>
            )}
            
            {isClubCoachContext && (
              <DropdownMenuItem onClick={() => navigate('/club-coach-dashboard')}>
                <LayoutDashboard className="mr-2 h-4 w-4" />
                My Dashboard
              </DropdownMenuItem>
            )}
            
            <DropdownMenuItem onClick={() => navigate('/athlete-dashboard/preferences')}>
              <Settings className="mr-2 h-4 w-4" />
              Preferences
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => navigate('/billing')}>
              <CreditCard className="mr-2 h-4 w-4" />
              Billing & Subscriptions
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem onClick={handleResetPassword} className="cursor-pointer">
              <Key className="mr-2 h-4 w-4" />
              Reset Password
            </DropdownMenuItem>
            
            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <AreaUserMenu settingsPath="/account" showName={false} />
        </div>
      </div>
    </div>
  );
};

export default Navbar;